// Campaign tags (utm_*) of the address the visitor arrived with.
// Nothing is written to the device: the tags are read from the address bar and
// carried in the links to other pages of this site, so the next page still has
// them. Without this, a link such as /#pricing on the demo page would start a
// new visit with no tags.
export const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
];

const MAX_VALUE_LENGTH = 100;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

// Only the known keys, trimmed, without control characters, at most 100
// characters each (a Paddle passthrough is limited to 1000 characters in all).
export const readCampaign = (search) => {
  const params = new URLSearchParams(
    search === undefined && typeof window !== 'undefined'
      ? window.location.search
      : search || ''
  );
  const campaign = {};
  CAMPAIGN_KEYS.forEach((key) => {
    const value = (params.get(key) || '')
      .replace(CONTROL_CHARS, '')
      .trim()
      .slice(0, MAX_VALUE_LENGTH);
    if (value) campaign[key] = value;
  });
  return campaign;
};

// The same href with the campaign tags added, or the href unchanged when it
// has nothing to add: no tags, a link to the same page (#anchor), another
// site, a mailto:, or a link that already carries its own tags.
export const withCampaign = (href, campaign, base) => {
  if (!href || href.charAt(0) === '#' || !Object.keys(campaign).length) {
    return href;
  }
  let url;
  try {
    url = new URL(href, base);
  } catch (e) {
    return href;
  }
  if (url.origin !== new URL(base).origin) return href;
  if (CAMPAIGN_KEYS.some((key) => url.searchParams.has(key))) return href;
  Object.keys(campaign).forEach((key) => url.searchParams.set(key, campaign[key]));
  return `${url.pathname}${url.search}${url.hash}`;
};

// Adds the tags to every link to this site, now and for links added later
// (buttons render after the page loads), also the buttons of the demo frame
// (/demo-app/, same site) that lead back to the home page ("Try Pro free",
// "See pricing"). Does nothing without tags in the address, so
// ordinary visits see no change at all.
export const installCampaignCarrier = () => {
  if (typeof window === 'undefined' || window.__cpCarrier) return;
  const campaign = readCampaign();
  if (!Object.keys(campaign).length) return;
  window.__cpCarrier = true;

  const rewrite = (anchor) => {
    // In the demo frame only the buttons that leave it (target="_top"); its
    // own menu links are routes of the demo, not pages of the site.
    if (anchor.ownerDocument !== document && anchor.target !== '_top') return;
    const href = anchor.getAttribute('href');
    const next = withCampaign(href, campaign, anchor.ownerDocument.baseURI);
    if (next !== href) anchor.setAttribute('href', next);
  };

  const watched = new WeakSet();
  const watchDocument = (doc) => {
    if (!doc || !doc.body || watched.has(doc)) return;
    watched.add(doc);
    // eslint-disable-next-line no-use-before-define
    scan(doc.body);
    new MutationObserver((records) => {
      records.forEach((record) => {
        // eslint-disable-next-line no-use-before-define
        if (record.type === 'attributes') rewrite(record.target);
        // eslint-disable-next-line no-use-before-define
        else record.addedNodes.forEach(scan);
      });
    }).observe(doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  };
  const watchFrame = (frame) => {
    const attach = () => {
      try {
        watchDocument(frame.contentDocument);
      } catch (e) {
        // another site: nothing to carry
      }
    };
    frame.addEventListener('load', attach);
    attach();
  };
  const scan = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches('a[href]')) rewrite(node);
    node.querySelectorAll('a[href]').forEach(rewrite);
    if (node.matches('iframe')) watchFrame(node);
    node.querySelectorAll('iframe').forEach(watchFrame);
  };

  watchDocument(document);
};
